import { useEffect, useState } from 'react';
import { Icon } from '../chrome/Icon';
import { Sheet } from '../shared/Sheet';
import { useT } from '../../i18n';
import { ApiError } from '../../api';
import { useAcceptInvite, useInvitePreview } from '../../data/queries';
import { parseInviteToken } from '../../data/pendingInvite';
import type { BabyAccessRole } from '../../data/dto';

interface Props {
  open: boolean;
  /** Pre-fill the code (e.g. from a deep link). */
  initialCode?: string | null;
  onClose: () => void;
  /** Called with the joined baby's id so the caller can make it active. */
  onJoined: (babyId: string) => void;
}

function roleLabel(t: ReturnType<typeof useT>, role: BabyAccessRole | undefined): string {
  switch (role) {
    case 'editor':      return t('sharing.role.editor', 'Partner / Editor');
    case 'contributor': return t('sharing.role.contributor', 'Can add photos & comments');
    case 'viewer':      return t('sharing.role.viewer', 'Read only');
    default:            return '';
  }
}

/**
 * Join a baby someone else shared, by pasting the invite link or typing the
 * code. Live-previews the baby's name + the role you'll get before you commit.
 */
export function JoinBabySheet({ open, initialCode, onClose, onJoined }: Props) {
  const t = useT();
  const [code, setCode] = useState(initialCode ?? '');

  // Re-seed when reopened with a different prefilled code.
  useEffect(() => {
    if (open) setCode(initialCode ?? '');
  }, [open, initialCode]);

  // Tokens may be pasted as a full URL — pull the ?invite= param out.
  const token = parseInviteToken(code);
  const preview = useInvitePreview(open && token ? token : null);
  const accept = useAcceptInvite();

  const valid = preview.data?.valid === true;

  const submit = () => {
    if (!token || accept.isPending) return;
    accept.mutate(token, {
      onSuccess: (r) => {
        onJoined(r.baby.id);
        onClose();
      },
    });
  };

  const acceptError =
    accept.error instanceof ApiError
      ? t('join.invalid', "That code didn't work — check it and try again.")
      : accept.error
      ? t('join.failed', 'Something went wrong. Please try again.')
      : null;

  return (
    <Sheet open={open} onClose={onClose} size="md" maxHeight="88dvh" label={t('join.title', 'Join a baby')}>
      <div className="flex flex-col gap-6 px-6 pb-8 pt-0 overflow-y-auto">
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('join.title', 'Join a baby')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        <div>
          <h1 className="font-serif text-[26px] leading-tight tracking-tight text-ink">
            {t('join.heading', 'Join with an invite')}
          </h1>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-2 text-pretty">
            {t('join.desc', 'Paste the link or enter the code a parent shared with you.')}
          </p>
        </div>

        <div>
          <label htmlFor="join-code" className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('join.codeLabel', 'Invite link or code')}
          </label>
          <input
            id="join-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('join.codePlaceholder', 'Paste link or code')}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="mt-1.5 w-full min-h-12 rounded-xl border border-line bg-surface px-4 py-3 font-mono text-[14px] text-ink outline-none placeholder:font-sans placeholder:text-ink-3 focus:border-ink transition-colors"
          />
        </div>

        {/* Live preview */}
        {token && preview.isFetching && !preview.data && (
          <p className="text-[13px] text-ink-2">{t('join.checking', 'Checking…')}</p>
        )}
        {token && preview.data && !valid && (
          <p className="rounded-xl px-3 py-2.5 text-[12.5px] leading-snug" style={{ background: 'rgba(181, 82, 59, 0.10)', border: '1px solid rgba(181, 82, 59, 0.22)', color: '#7E3320' }}>
            {t('join.notFound', "This invite is invalid or has expired.")}
          </p>
        )}
        {valid && (
          <div className="rounded-2xl border border-line bg-surface px-4 py-3.5">
            <p className="text-[14.5px] text-ink">
              {t('join.willJoin', "You'll join ")}
              <span className="font-serif font-semibold">{preview.data!.baby_name}</span>
            </p>
            <p className="mt-1 text-[12.5px] text-ink-2">
              {t('join.asRole', 'as: {role}').replace('{role}', roleLabel(t, preview.data!.role))}
            </p>
          </div>
        )}

        {acceptError && (
          <p role="alert" className="rounded-xl px-3 py-2.5 text-[12.5px] leading-snug" style={{ background: 'rgba(181, 82, 59, 0.10)', border: '1px solid rgba(181, 82, 59, 0.22)', color: '#7E3320' }}>
            {acceptError}
          </p>
        )}

        <button
          onClick={submit}
          disabled={!valid || accept.isPending}
          className="inline-flex w-full min-h-14 items-center justify-center gap-2.5 rounded-3xl bg-ink px-6 text-[15.5px] font-semibold text-bg disabled:opacity-50"
        >
          {accept.isPending ? t('join.joining', 'Joining…') : t('join.joinBtn', 'Join baby')}
          {!accept.isPending && <Icon name="arrow" size={18} color="currentColor" />}
        </button>
      </div>
    </Sheet>
  );
}
